import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getTradeActivitySubTrades } from 'app/state/ducks/trade-detail/selectors'
import { getActivityItems } from 'app/state/ducks/trade-notes/selectors'
import {
  ActivityItem,
  ActivityStreamItem,
  TradeNoteItem,
  sortActivityItems,
} from 'app/state/ducks/trade-notes/types'
import {
  addTradeNote,
  addTradeNoteToAll,
  markAllNotesActioned,
  markNoteRead,
  reopenNote,
  refreshMasterTradeStream,
} from './activity-stream-daml-actions'

export type SubTradeOption = { value: string; label: string }

/** The stream reads newest-first by default, as the design specifies; the control toggles it. */
export type ActivityStreamSortOrder = 'newest' | 'oldest'

/** The Filters panel offers exactly one group in Phase 1: the source system. */
export type ActivitySourceFilter = 'All' | 'LendOS' | 'SigningPortal'

/**
 * The design prototype caps a note at 2000 characters (the composer's placeholder says so out
 * loud). A frontend courtesy only — nothing on the ledger enforces it.
 */
export const MAX_NOTE_LENGTH = 2_000

/**
 * Sentinel for the composer's "All sub-trades (N)" option: the note is posted once per
 * sub-trade. Deliberately not a valid StableKey shape.
 */
export const ALL_SUB_TRADES = '__all'

/**
 * The composer's Quick Note options (LOS-6990): routine requests, picked instead of retyped so
 * the wording stays the same across the team. The Signing Portal offers the same three.
 */
export const QUICK_NOTES = [
  'Administrative Details Form (ADF) required from buyer',
  'Tax form required from buyer',
  'Affiliate letter required from buyer',
] as const

/**
 * A Quick Note pick appends to what is already written: on a new line when there is text,
 * after dropping trailing whitespace so repeated picks never leave blank lines between them.
 */
export const appendQuickNote = (body: string, text: string): string => {
  const kept = body.replace(/\s+$/, '')
  return kept ? `${kept}\n${text}` : text
}

/**
 * The textarea's maxLength only stops typing, not text set in code, so an option that would
 * take the note past the limit is offered disabled instead.
 */
export const quickNoteFits = (body: string, text: string): boolean =>
  appendQuickNote(body, text).length <= MAX_NOTE_LENGTH

/**
 * `sortActivityItems` is the stable oldest-first order (ties broken on id so the list cannot
 * shuffle between refreshes). Newest-first is that order reversed, which keeps the tie-break —
 * sorting descending directly would not.
 */
export const orderActivityItems = <T extends ActivityStreamItem>(
  items: T[],
  order: ActivityStreamSortOrder,
): T[] => {
  const oldestFirst = sortActivityItems(items)
  return order === 'newest' ? oldestFirst.reverse() : oldestFirst
}

/**
 * The design's search box. Client-side, over the two fields a reader can actually see: what was
 * written and who wrote it.
 */
export const matchesActivitySearch = (item: ActivityItem, search: string): boolean => {
  const term = search.trim().toLowerCase()
  if (!term) return true
  return (
    (item.body ?? '').toLowerCase().includes(term) ||
    (item.authorName ?? '').toLowerCase().includes(term)
  )
}

/**
 * "New" on LendOS = portal-authored and unreceipted (read-state design). LendOS-authored notes
 * are never New here — the mirror rule is enforced by both backends and the ledger. `readAt`
 * is the discriminator, not the reader id: a receipt always carries a time, while the auth0
 * id is optional on the ledger.
 */
export const isNewOnLendos = (item: ActivityItem): boolean =>
  item.kind === 'note' && item.source === 'SigningPortal' && !item.readAt

export const matchesSourceFilter = (
  item: ActivityItem,
  filter: ActivitySourceFilter,
): boolean => filter === 'All' || item.source === filter

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "Aug 19, 2026 @ 9:38 AM" — the design prototype's timestamp format (note the `@`; the earlier
 * Figma frames used "at"). Built by hand rather than via toLocaleString so the SHAPE is
 * identical for every viewer regardless of locale settings.
 *
 * The CLOCK VALUE is deliberately the viewer's local time — the convention every timestamp in
 * both products follows (hold "Placed On", My Work due times, the trade-list header clock).
 * Rendering this one surface in UTC would disagree with everything on the same screen. Viewers
 * in different time zones therefore see different clocks for the same note; the row exposes
 * the absolute ISO instant as a hover tooltip for when that matters cross-zone.
 */
export const formatActivityTimestamp = (iso: string): string => {
  const parsed = new Date(iso)
  if (isNaN(parsed.getTime())) return iso
  const hours = parsed.getHours()
  const h12 = hours % 12 === 0 ? 12 : hours % 12
  const minutes = String(parsed.getMinutes()).padStart(2, '0')
  const meridiem = hours < 12 ? 'AM' : 'PM'
  return `${MONTHS[parsed.getMonth()]} ${parsed.getDate()}, ${parsed.getFullYear()} @ ${h12}:${minutes} ${meridiem}`
}

/**
 * The attribution line that replaces "Mark as actioned" once a note is receipted (LOS-6987):
 * "Actioned by {First Last} · {date} @ {time}", or "Actioned · {date} @ {time}" when the
 * reader's name could not be resolved.
 *
 * Only for notes LendOS actions — portal-authored ones. A LendOS-authored note can carry a
 * receipt too, but that is the PORTAL's acknowledgement, and each surface shows only its own.
 */
export const actionedLine = (item: ActivityItem): string | null => {
  if (item.kind !== 'note' || item.source !== 'SigningPortal' || !item.readAt) return null
  const when = formatActivityTimestamp(item.readAt)
  return item.readByName ? `Actioned by ${item.readByName} · ${when}` : `Actioned · ${when}`
}

/**
 * The line an undone note shows above "Mark as actioned" (LOS-6988): "Reopened by {First Last} ·
 * {date} @ {time}", or "Reopened · {date} @ {time}" with no resolvable name. The footer shows
 * only the most recent event (product decision), so this is null once the note is actioned
 * again — the backend clears the reopen then too. Same side rule as actionedLine: only the notes
 * LendOS acknowledges, portal-authored ones.
 */
export const reopenedLine = (item: ActivityItem): string | null => {
  if (item.kind !== 'note' || item.source !== 'SigningPortal') return null
  if (item.readAt || !item.reopenedAt) return null
  const when = formatActivityTimestamp(item.reopenedAt)
  return item.reopenedByName ? `Reopened by ${item.reopenedByName} · ${when}` : `Reopened · ${when}`
}

/**
 * State and side effects for the master-trade Activity Stream tab (ADR_LOS-028), built to the
 * design prototype (`activity-stream/Activity Stream.dc.html`) in its notes-only mode, per the
 * design brief (`activity-stream/LendOS Activity Stream page (Notes Phase 1).md`).
 *
 * Phase 1 is manual refresh: the stream arrives with the tab's own read (the page shell loads
 * it with the trade, LOS-7095) and reloads on an explicit refresh. There is no polling and no
 * subscription — Phase 2's ledger-event spine signals into exactly this reload.
 *
 * The brief's notes-only scope drops the role badge and the "Directed to" line from the note
 * row (rev 5 derived both from the master trade's party map). That also dissolves the rev-5
 * LendOS/SP asymmetry: neither surface shows them in Phase 1.
 */
export const useActivityStream = (masterTradeId: string | undefined) => {
  const dispatch = useDispatch()

  const allItems = useSelector(getActivityItems)
  const activitySubTrades = useSelector((state: any) =>
    getTradeActivitySubTrades(state, masterTradeId),
  )

  const [body, setBody] = useState('')
  const [selectedSubTradeId, setSelectedSubTradeId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sortOrder, setSortOrder] = useState<ActivityStreamSortOrder>('newest')
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState<ActivitySourceFilter>('All')
  const [filtersOpen, setFiltersOpen] = useState(false)
  // The design puts the composer behind "+ Add Note" rather than always on screen, so the
  // stream itself is what the tab opens on.
  const [isComposerOpen, setIsComposerOpen] = useState(false)
  const [isMarkingAll, setIsMarkingAll] = useState(false)

  /**
   * Notes attach to a sub-trade, not a master trade, so composing one needs a target.
   *
   * The tab's own read (LOS-7095) carries this trade's sub trades, open and settled, in the
   * picker's order (systemId, then id) — settled sub-trades are offered too: correspondence
   * about a settled trade is exactly when notes matter, and the backend resolves the locked
   * variant for the same reason.
   */
  const subTradeOptions: SubTradeOption[] = useMemo(() => {
    // Once per id: the read unions open and settled sub trades, and a sub trade can never be
    // both (settling archives it in the same transaction) — but a duplicate would give the
    // Select two options with one value and stop a one-sub-trade trade auto-selecting
    // (review on #8472).
    const byId = new Map(activitySubTrades.map(st => [st.id, st]))
    return Array.from(byId.values()).map(st => ({
      value: st.id,
      label: st.systemId || st.id,
    }))
  }, [activitySubTrades])

  // A non-fund-manager trade has exactly one sub-trade, which is the common case — pick it so
  // the author never has to choose. With several, the choice is theirs and stays required.
  useEffect(() => {
    if (subTradeOptions.length === 1) {
      setSelectedSubTradeId(subTradeOptions[0].value)
      return
    }
    setSelectedSubTradeId(current =>
      current && (current === ALL_SUB_TRADES || subTradeOptions.some(o => o.value === current))
        ? current
        : null,
    )
  }, [subTradeOptions])

  const tradeItems: ActivityItem[] = useMemo(
    () => (allItems ?? []).filter(item => item.masterTradeId === masterTradeId),
    [allItems, masterTradeId],
  )

  const items: ActivityItem[] = useMemo(
    () =>
      orderActivityItems(
        tradeItems.filter(
          item => matchesSourceFilter(item, sourceFilter) && matchesActivitySearch(item, search),
        ),
        sortOrder,
      ),
    [tradeItems, sourceFilter, search, sortOrder],
  )

  const refresh = useCallback(async () => {
    if (!masterTradeId) return
    setIsLoading(true)
    try {
      await refreshMasterTradeStream(masterTradeId, dispatch)
    } finally {
      setIsLoading(false)
    }
  }, [masterTradeId, dispatch])

  const toggleSortOrder = useCallback(() => {
    setSortOrder(current => (current === 'newest' ? 'oldest' : 'newest'))
  }, [])

  const toggleFilters = useCallback(() => {
    setFiltersOpen(current => !current)
  }, [])

  const clearFilters = useCallback(() => {
    setSourceFilter('All')
  }, [])

  const markRead = useCallback(
    (item: TradeNoteItem) => markNoteRead(item, dispatch),
    [dispatch],
  )

  const reopen = useCallback(
    (item: TradeNoteItem) => reopenNote(item, dispatch),
    [dispatch],
  )

  /**
   * The unactioned portal-authored notes the user can SEE — the search/source-filtered view, not
   * the whole trade (product decisions in LOS-6989). They are both what "Mark all N as actioned"
   * covers, so a note filtered out of view is never acknowledged unseen, and what the
   * "{n} need(s) action" pill counts. The trade-level badges (the tab, the trade list's Stream
   * column) still count the whole trade — they come from the unread-counts slice.
   */
  const actionableItems = useMemo(() => items.filter(isNewOnLendos), [items])

  const markAllActioned = useCallback(async () => {
    if (!masterTradeId || isMarkingAll || actionableItems.length === 0) return
    setIsMarkingAll(true)
    try {
      await markAllNotesActioned(masterTradeId, actionableItems, dispatch)
    } finally {
      setIsMarkingAll(false)
    }
  }, [masterTradeId, isMarkingAll, actionableItems, dispatch])

  const openComposer = useCallback(() => {
    setIsComposerOpen(true)
  }, [])

  const cancelComposer = useCallback(() => {
    setIsComposerOpen(false)
    setBody('')
  }, [])

  const quickNoteOptions = useMemo(
    () => QUICK_NOTES.map(text => ({ value: text, label: text, disabled: !quickNoteFits(body, text) })),
    [body],
  )

  const addQuickNote = useCallback((text: string | null) => {
    if (!text) return
    setBody(current => (quickNoteFits(current, text) ? appendQuickNote(current, text) : current))
  }, [])

  const trimmedBody = body.trim()
  const canSubmit =
    !!selectedSubTradeId &&
    (selectedSubTradeId !== ALL_SUB_TRADES || subTradeOptions.length > 0) &&
    trimmedBody.length > 0 &&
    trimmedBody.length <= MAX_NOTE_LENGTH &&
    !isSubmitting

  const submit = useCallback(async () => {
    if (!canSubmit || !selectedSubTradeId) return
    setIsSubmitting(true)
    try {
      // Both paths land in the store via the create thunk's upsert, so no refetch is needed.
      const created =
        selectedSubTradeId === ALL_SUB_TRADES
          ? await addTradeNoteToAll(
              subTradeOptions.map(o => o.value),
              trimmedBody,
              dispatch,
            )
          : Boolean(await addTradeNote(selectedSubTradeId, trimmedBody, dispatch))
      if (created) {
        setBody('')
        setIsComposerOpen(false)
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [canSubmit, selectedSubTradeId, subTradeOptions, trimmedBody, dispatch])

  return {
    items,
    /** Count of the trade's items before search/filter narrowing — tells empty states apart. */
    totalCount: tradeItems.length,
    isLoading,
    isSubmitting,
    refresh,
    body,
    setBody,
    quickNoteOptions,
    addQuickNote,
    subTradeOptions,
    selectedSubTradeId,
    setSelectedSubTradeId,
    canSubmit,
    submit,
    sortOrder,
    toggleSortOrder,
    markRead,
    reopen,
    /** The visible unactioned notes: the pill's count and N in "Mark all N as actioned". */
    unactionedCount: actionableItems.length,
    markAllActioned,
    isMarkingAll,
    search,
    setSearch,
    sourceFilter,
    setSourceFilter,
    filtersOpen,
    toggleFilters,
    clearFilters,
    isComposerOpen,
    openComposer,
    cancelComposer,
  }
}

export type ActivityStreamViewModel = ReturnType<typeof useActivityStream>
